import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Bouton — refonte « Nourri ».
 *
 * Le prototype (`design/nutri-redesign.html`) porte huit classes de bouton :
 * `.primary-btn`, `.btn-ghost-cta`, `.seg-btn`, `.pill-btn`, `.round-btn`,
 * `.icon-btn`, `.add-btn`, `.add-pill`. Ce ne sont pas huit composants mais
 * **deux axes croisés** — ce que le bouton pèse (`variant`) et la forme qu'il
 * prend (`shape`) —, parce que le prototype les croise déjà librement : un
 * rond peut être plein ou bordé, une pilule aussi.
 *
 * | Prototype        | Ici                                        |
 * |------------------|--------------------------------------------|
 * | `.primary-btn`   | `variant="primary" size="lg" block`        |
 * | `.add-btn`       | `variant="primary" shape="circle"`         |
 * | `.round-btn`     | `variant="secondary" shape="circle"`       |
 * | `.icon-btn`      | `variant="ghost" shape="circle"`           |
 * | `.btn-ghost-cta` | `variant="ghost" shape="rounded"`          |
 * | `.seg-btn`       | `variant="secondary" shape="rounded"`      |
 * | `.seg-btn.active`| `variant="selected" shape="rounded"`       |
 * | `.pill-btn`      | `variant="danger"`                         |
 *
 * ⚠️ **`shape="circle"` n'a pas de libellé visible** : lui passer `aria-label`
 * n'est pas une option de confort, c'est la seule chose qui le nomme. Un
 * glyphe n'a pas de nom accessible — même règle que les icônes d'onglets
 * depuis l'étape 5, où le texte porte le nom et l'icône est `aria-hidden`.
 *
 * ⚠️ Les couleurs et les rayons vivent tous dans `styles/nourri.css`, qui ne
 * lit que les jetons. Ne rien repasser en `style={{}}` : c'est ce que la V1
 * a mis dix étapes à démêler.
 */
export function Button({
  children,
  variant = 'secondary',
  shape = 'pill',
  size = 'md',
  block = false,
  className,
  type = 'button',
  ...rest
}: {
  children: ReactNode;
  /** Ce que le bouton pèse à l'écran. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'selected' | 'danger';
  /** Sa forme. `circle` attend un `aria-label`. */
  shape?: 'pill' | 'rounded' | 'circle';
  size?: 'md' | 'lg';
  /** Pleine largeur — le bouton de validation en pied de feuille. */
  block?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & { className?: string }) {
  const classes = ['n-btn', `n-btn--${variant}`];
  if (shape !== 'pill') classes.push(`n-btn--${shape}`);
  if (size !== 'md') classes.push(`n-btn--${size}`);
  if (block) classes.push('n-btn--block');
  if (className) classes.push(className);

  return (
    <button type={type} className={classes.join(' ')} {...rest}>
      {children}
    </button>
  );
}
